/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type LivePersonConfiguration from 'embercom/models/standalone/live-person-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  stackVertically?: boolean;
  hideBorder?: boolean;
  hideTitle?: boolean;
  closeOnSave?: () => void;
  closeOnRevoke?: () => void;
  livePersonConfig: LivePersonConfiguration;
}

export default class StandaloneLivePersonConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  @tracked isSaving = false;

  get isSaveDisabled() {
    return (
      this.isSaving ||
      !this.livePersonConfig.canUpdateAPISettings ||
      !this.livePersonConfig.isFormValid
    );
  }

  get livePersonConfig(): LivePersonConfiguration {
    return this.args.livePersonConfig;
  }

  @action async save() {
    // Only allow saving during pending setup (initial configuration)
    if (!this.livePersonConfig.isPendingSetup) {
      return;
    }

    this.isSaving = true;
    try {
      await this.livePersonConfig.save();

      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.deploy.api.live_person.notifications.save_success'),
      );
      this.args.closeOnSave?.();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        { default: this.intl.t('standalone.deploy.api.live_person.notifications.save_error') },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action async revokeAccess() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.deploy.api.live_person.settings.confirm_revoke'),
      confirmButtonText: this.intl.t('standalone.deploy.api.live_person.settings.confirm_button'),
    });

    try {
      if (isConfirmed) {
        await this.livePersonConfig.revoke();

        if (this.args.closeOnRevoke) {
          this.args.closeOnRevoke();
        }
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.deploy.api.live_person.notifications.revoke_success'),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        { default: this.intl.t('standalone.deploy.api.live_person.notifications.revoke_error') },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::LivePerson::Settings::Connect': typeof StandaloneLivePersonConnect;
    'standalone/live-person/settings/connect': typeof StandaloneLivePersonConnect;
  }
}
